import React, { useEffect, useMemo, useState } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockDes,
} from "../../../components/Component";
import { Card, Input, Spinner } from "reactstrap";
import swal from "sweetalert";
import http from "../../../utils/http-common";
import { canManageAdmins } from "../../../utils/adminPermissions";

function tagClass(action) {
  if (/refund|delete|ban/i.test(action)) return "badge-danger";
  if (/permission|invite/i.test(action)) return "badge-warning";
  return "badge-light";
}

function fmtWhen(d) {
  if (!d) return "—";
  const dt = new Date(d);
  return dt.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function RbacAudit() {
  const me = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);
  const allowed = canManageAdmins(me);
  const [rows, setRows] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const [from, setFrom] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (actor) params.actor = actor;
      if (action) params.action = action;
      if (from) params.from = from;
      const [audit, adminRes] = await Promise.all([
        http.get("/api/v1/admin/rbac/audit", { params }),
        http.get("/api/v1/admin/rbac/admins"),
      ]);
      setRows(audit.data?.data || []);
      setAdmins((adminRes.data?.data || []).filter((a) => !String(a.id).startsWith("invite:")));
    } catch (e) {
      swal("Error", e.response?.data?.error || "Failed to load audit", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allowed) load();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, actor, action, from]);

  if (!allowed) {
    return (
      <>
        <Head title="Audit log" />
        <Content>
          <Block>
            <p className="text-soft">You do not have permission to view the audit log.</p>
          </Block>
        </Content>
      </>
    );
  }

  return (
    <>
      <Head title="Audit log" />
      <Content>
        <BlockHead>
          <BlockHeadContent>
            <BlockTitle page>Audit log</BlockTitle>
            <BlockDes>
              <p>Every sensitive action, recorded and read-only.</p>
            </BlockDes>
          </BlockHeadContent>
        </BlockHead>

        <div className="d-flex flex-wrap mb-3" style={{ gap: 8 }}>
          <Input type="select" style={{ maxWidth: 200 }} value={actor} onChange={(e) => setActor(e.target.value)}>
            <option value="">All admins</option>
            {admins.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Input>
          <Input type="select" style={{ maxWidth: 200 }} value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="">All actions</option>
            <option value="refund">Refund</option>
            <option value="permissions">Permissions</option>
            <option value="admin_invite">Invite</option>
            <option value="admin_delete">Delete admin</option>
            <option value="admin_disable">Disable</option>
          </Input>
          <Input type="date" style={{ maxWidth: 180 }} value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>

        {loading ? (
          <Spinner />
        ) : (
          <Card className="card-bordered">
            <div
              className="d-none d-md-grid px-3 py-2 bg-lighter text-soft small text-uppercase font-weight-bold"
              style={{ gridTemplateColumns: "1.1fr 1fr 1.3fr 1fr", gap: 8 }}
            >
              <span>When</span>
              <span>Admin</span>
              <span>Action</span>
              <span>Target</span>
            </div>
            {(rows || []).map((r) => (
              <div
                key={r.id}
                className="d-grid px-3 py-3 border-top"
                style={{ gridTemplateColumns: "1.1fr 1fr 1.3fr 1fr", gap: 8, fontSize: 13 }}
              >
                <span className="text-soft">{fmtWhen(r.when)}</span>
                <span>{r.actor?.name || "—"}</span>
                <span>
                  <span className={`badge ${tagClass(r.action)}`}>{r.action}</span>
                </span>
                <span className="text-soft">
                  {r.targetType}
                  {r.targetId ? ` · ${String(r.targetId).slice(0, 8)}…` : ""}
                </span>
              </div>
            ))}
            {!rows.length ? (
              <p className="text-soft text-center py-4 mb-0">No audit entries yet.</p>
            ) : null}
          </Card>
        )}
        <p className="text-soft small mt-2">Entries cannot be edited or deleted. Filters: admin · action · period.</p>
      </Content>
    </>
  );
}
